/** Utilitários de CNPJ/CPF — puros, sem DOM. O CNPJ pode ser numérico ou alfanumérico (IN RFB 2.229/2024); o CPF é numérico. */

export const soDigitos = (v: string | null | undefined): string => (v || '').replace(/\D/g, '');

function digitoVerificador(base: number[] | string, pesos: number[]): number {
  const digitos = typeof base === 'string' ? base.split('').map(Number) : base;
  const soma = digitos.reduce((s, d, i) => s + d * pesos[i], 0);
  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
}

/** CNPJ (numérico ou alfanumérico, IN RFB 2.229/2024): 12 posições [0-9A-Z] + 2 dígitos verificadores numéricos. */
export const normalizarCnpj = (v: string | null | undefined): string => (v || '').toUpperCase().replace(/[^0-9A-Z]/g, '');

/** Valor de cada caractere no cálculo do DV: código ASCII − 48 (dígitos 0–9; letras A=17 … Z=42). */
const valorCnpj = (ch: string) => ch.charCodeAt(0) - 48;

export function cnpjValido(valor: string | null | undefined): boolean {
  const c = normalizarCnpj(valor);
  if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(c) || /^(.)\1{13}$/.test(c)) return false;
  const base = c.slice(0, 12).split('').map(valorCnpj);
  const d1 = digitoVerificador(base, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const d2 = digitoVerificador([...base, d1], [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  return d1 === Number(c[12]) && d2 === Number(c[13]);
}

export function cpfValido(valor: string | null | undefined): boolean {
  const c = soDigitos(valor);
  if (c.length !== 11 || /^(\d)\1{10}$/.test(c)) return false;
  const d1 = digitoVerificador(c.slice(0, 9), [10, 9, 8, 7, 6, 5, 4, 3, 2]);
  const d2 = digitoVerificador(c.slice(0, 10), [11, 10, 9, 8, 7, 6, 5, 4, 3, 2]);
  return d1 === Number(c[9]) && d2 === Number(c[10]);
}

export function formatarCnpj(valor: string | null | undefined): string {
  const c = normalizarCnpj(valor);
  if (c.length !== 14) return c;
  return `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}`;
}

/** Documento de cliente (CPF ou CNPJ) como chave: maiúsculas, sem máscara. Numéricos ficam idênticos a soDigitos. */
export const normalizarDocumento = normalizarCnpj;

/** Formato de CPF (11 dígitos) ou CNPJ (12 posições [0-9A-Z] + 2 dígitos), sem conferir o dígito verificador. */
export function documentoFormatoValido(valor: string | null | undefined): boolean {
  const d = normalizarDocumento(valor);
  return /^\d{11}$/.test(d) || /^[0-9A-Z]{12}\d{2}$/.test(d);
}

export function formatarDocumento(valor: string | null | undefined): string {
  const d = normalizarDocumento(valor);
  if (d.length === 14) return formatarCnpj(d);
  if (d.length === 11 && /^\d{11}$/.test(d)) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  return d;
}

/** Chave de acesso de documento eletrônico: 44 posições; alfanumérica a partir do leiaute 2026 (a posição do CNPJ). */
export const CHAVE_ACESSO_RX = /^[0-9A-Z]{44}$/;

/**
 * DV da chave de acesso (módulo 11, pesos 2 a 9 da direita para a esquerda sobre as 43 primeiras posições; cada
 * posição vale o código ASCII − 48, então a posição do CNPJ pode ser alfanumérica). Resto 0 ou 1 → 0.
 */
export function chaveAcessoDv(base43: string): number {
  let soma = 0;
  let peso = 2;
  for (let i = base43.length - 1; i >= 0; i--) {
    soma += (base43.charCodeAt(i) - 48) * peso;
    peso = peso === 9 ? 2 : peso + 1;
  }
  const resto = soma % 11;
  return resto === 0 || resto === 1 ? 0 : 11 - resto;
}

export function chaveAcessoDvValido(chave: string | null | undefined): boolean {
  const c = (chave || '').toUpperCase();
  return CHAVE_ACESSO_RX.test(c) && chaveAcessoDv(c.slice(0, 43)) === Number(c[43]);
}

/** Partes da chave: cUF(2) AAMM(4) CNPJ(14) mod(2) série(3) nNF(9) tpEmis(1) cNF(8) cDV(1). */
export function partesChave(chave: string) {
  return { cuf: chave.slice(0, 2), aamm: chave.slice(2, 6), cnpj: chave.slice(6, 20), modelo: chave.slice(20, 22), serie: chave.slice(22, 25), numero: chave.slice(25, 34) };
}
